/* oxlint-disable no-use-before-define */
import { DisconnectedError } from 'DisconnectedError';
import { statsigClientProvider } from 'StatsigClientProvider';
import { StudentDashboardContentProvider } from 'StoredContent';
import { shoveProgressIntoContent } from 'LessonProgress';
import { getCohort } from 'Users';
import cohortKeysForStudentDashboardFetch from 'FrontRoyalStore/cohortKeysForStudentDashboardFetch';
import { SuspendableResource } from 'useSuspendableResource';
import delay from 'delay';
import { buildStudentDashboardShowParams, fetchStudentDashboard } from 'StudentDashboardContent';
import { streamToIguanaAttrs } from 'Lessons/stream';
import { playlistToIguanaAttrs } from 'Playlists';
import angularModule from './learner_content_cache_module';

angularModule.factory('LearnerContentCache', [
    '$injector',
    $injector => {
        const $rootScope = $injector.get('$rootScope');
        const studentDashboardContentProvider = new StudentDashboardContentProvider($injector);
        const ConfigFactory = $injector.get('ConfigFactory');

        // "legacy" here means that we're converting things to iguana objects.
        // This function is only triggered when loading via tanstack. When loading
        // via the StudentDashbaordContentProvider, either the getStudentDashboardFromStore method
        // or Iguana itself handles this.
        const createLegacyStudentDashboardResult = contents => {
            const Stream = $injector.get('Lesson.Stream');
            const Playlist = $injector.get('Playlist');

            // When tanstack loading is enabled the stream here is an instance of StreamForDashboard
            // and does not have all of the properties we would normally expect in a Stream
            // iguana object, meaning we just have to assume that all the usages of these objects
            // will be ok with that. But, this is legacy code so we're going with it like this for now.
            // These objects do, however, include locale packs even though StreamForDashboard says they
            // don't. See comment at StudentDashboardsController#minimal_stream_json
            const legacyStreams = (contents.lessonStreams || []).map(stream => Stream.new(streamToIguanaAttrs(stream)));

            const legacyPlaylists = (contents.availablePlaylists || []).map(playlist =>
                Playlist.new(playlistToIguanaAttrs(playlist)),
            );

            return {
                result: [
                    {
                        lesson_streams: legacyStreams,
                        available_playlists: legacyPlaylists,
                    },
                ],
            };
        };

        // This function is called from both the TanStack and StudentDashboardContentProvider
        // to support legacy UI's that still expect to be able to read progress off of
        // stream and lesson iguana objects via, for example `lesson.lesson_progess`
        const addProgressAndReturn = ({ response, includeProgress, user }) => {
            if (!user || user !== $rootScope.currentUser) return response;

            if (includeProgress) {
                return shoveProgressIntoContent(response.result[0].lesson_streams, user).then(() => response);
            }

            return response;
        };

        const fetchStudentDashboardViaTanStack = async ({ user, includeProgress }) => {
            const config = await ConfigFactory.getConfig();
            const accessGroupIds = user.groups.map(group => group.id);

            const params = buildStudentDashboardShowParams({
                accessGroupIds,
                prefLocale: user.pref_locale,
                contentViewsRefreshUpdatedAt: config.contentViewsRefreshUpdatedAt(),
                cohortId: user.relevantCohort?.id,
                institutionId: user.active_institution?.id,
            });

            const tanStackResponse = await fetchStudentDashboard(params);

            // "legacy" here means that the items have been converted to iguana objects. That's
            // just how the learner_content_cache formats things. It's really the learner_content_cache
            // itself that is legacy code
            const legacyResponse = createLegacyStudentDashboardResult(tanStackResponse.contents ?? {});
            return addProgressAndReturn({ response: legacyResponse, includeProgress, user });
        };

        const LearnerContentCache = {
            ensureStudentDashboard({ includeProgress = true, suspendIfNoUser = false, totalDelayedMS = 0 } = {}) {
                const user = $rootScope.currentUser;

                if (!user) {
                    if (suspendIfNoUser && totalDelayedMS < 5000) {
                        // delay if we don't have a user and this was called from a SuspendableResource.
                        // If after 5 seconds we still don't have a user, throw an error.
                        return delay(100).then(() =>
                            LearnerContentCache.ensureStudentDashboard({
                                includeProgress,
                                suspendIfNoUser,
                                totalDelayedMS: totalDelayedMS + 100,
                            }),
                        );
                    }
                    throw new Error('No user found');
                }

                return statsigClientProvider.waitForGate('content_loading_2025').then(shouldUseTanStack => {
                    if (shouldUseTanStack) {
                        return fetchStudentDashboardViaTanStack({ user, includeProgress });
                    }

                    const promise = ConfigFactory.getConfig().then(config =>
                        studentDashboardContentProvider.getStudentDashboardContent({
                            userId: user.id,
                            prefLocale: user.pref_locale,
                            contentViewsRefreshUpdatedAt: config.contentViewsRefreshUpdatedAt(),
                            favoriteLessonStreamLocalePackIds: user.favorite_lesson_stream_locale_packs.map(
                                lp => lp.id,
                            ),
                            ...cohortKeysForStudentDashboardFetch(getCohort(user)),
                        }),
                    );

                    return promise.then(response => addProgressAndReturn({ response, includeProgress, user }));
                });
            },

            async preloadStudentDashboard() {
                return this.ensureStudentDashboard().catch(err => {
                    // ensureStudentDashboard can throw a DisconnectedError if
                    // we enter offline mode while it is in flight.  Since preloadStudentDashboard
                    // is just a fire-and-forget call, we can ignore that error.
                    if (err.constructor !== DisconnectedError) {
                        throw err;
                    }
                });
            },

            recreateSuspendableResource() {
                this.suspendableResource?.destroy();
                this.suspendableResource = new SuspendableResource(() =>
                    this.ensureStudentDashboard({
                        includeProgress: false,
                        suspendIfNoUser: true,
                        totalDelayedMS: 0,
                    }),
                );
            },

            bustCache() {
                studentDashboardContentProvider.bustCache();
            },

            preloadSuspendableResourceOnCurrentUserChange() {
                $rootScope.$on('$destroy', () => {
                    LearnerContentCache.suspendableResource?.destroy();
                });

                $rootScope.$watch('currentUser', currentUser => {
                    // This is fixing an issue with Log In As by recreating the suspendable resource whenever the
                    // current user changes.  It feels like this shouldn't be necessary, because useSuspendableResource
                    // will notice that its data is stale and reload it, but the problem is that it will initially render
                    // with the old data, and if you have content cached from a different user, this can trigger the
                    // "No cached stream found for locale pack." error.
                    LearnerContentCache.recreateSuspendableResource();

                    if (!currentUser) return;

                    // Save a little time on the first navigation to the dashboard by ensuring we've already
                    // preloaded the promise from the student dashboard.
                    LearnerContentCache.suspendableResource.preload();
                });
            },
        };

        return LearnerContentCache;
    },
]);
